// Standalone "chat-embed" session lockdown.
//
// This build runs **only** through a Business Bot bridge and must NEVER pick
// up credentials from a parallel real Telegram Web session that may live in
// the same browser profile. We achieve that by:
//
//   1. Wiping all MTProto-related localStorage keys (`dc*_auth_key`,
//      `dc*_hash`, `dc*_server_salt`, the legacy `user_auth` blob, and any
//      `account<N>` slot whose dcId points at a real Telegram session and
//      that does NOT have a matching `bb_token_<N>` business bot token).
//   2. Deleting Telegram Web IndexedDB databases that may store cached
//      MTProto state (`tt-session`, `passcodes`, `gramjs`).
//   3. Skipping cross-tab BroadcastChannel sharing of `global` state so a
//      malicious or stale neighbour tab can't inject an authenticated global
//      into this tab.
//
// Origin-isolation already prevents leakage across different origins
// (localhost:1237 ≠ web.telegram.org), but same-origin tabs of this exact
// build still share storage — these guards address that case.

import { ACCOUNT_SLOT } from './multiaccount';

const MTPROTO_LS_KEYS = ['user_auth', 'dc'];
const ALLOWED_LS_PREFIXES = [
  'bb_token_', // business bot tokens
  'tt-multitab_', // multitab handshake markers (per slot)
  'tt-global-state', // cached global (we own it)
  'GramJs:', // gramjs internal cache (only used by bb client)
  'shared-settings', // shared settings cache
];

function isMtprotoKey(key: string): boolean {
  // dc1_auth_key, dc2_hash, dc1_server_salt, etc.
  if (/^dc\d+_(auth_key|hash|server_salt)$/.test(key)) return true;
  if (key === 'user_auth') return true;
  return false;
}

function isAccountSlotKey(key: string): boolean {
  return /^account\d+$/.test(key);
}

/**
 * Aggressively purge any MTProto-related leftovers from localStorage that
 * could otherwise be picked up at boot. Safe to call on every cold start.
 */
export function purgeMtprotoLeftovers() {
  const slot = ACCOUNT_SLOT || 1;
  const ourBbKey = `bb_token_${slot}`;

  // Snapshot keys first because we mutate localStorage during iteration.
  const keys = Object.keys(localStorage);
  for (const key of keys) {
    if (isMtprotoKey(key)) {
      localStorage.removeItem(key);
      continue;
    }
    if (isAccountSlotKey(key)) {
      // Keep only the `account<N>` slot for which we own a `bb_token_<N>`.
      const slotNum = key.slice('account'.length);
      const hasBbToken = !!localStorage.getItem(`bb_token_${slotNum}`);
      if (!hasBbToken) {
        localStorage.removeItem(key);
      }
      continue;
    }
  }

  // Make sure we still have our own bb token; if not, wipe the rest of our
  // namespace too so we start from a perfectly clean slate.
  if (!localStorage.getItem(ourBbKey)) {
    for (const key of Object.keys(localStorage)) {
      if (ALLOWED_LS_PREFIXES.some((p) => key.startsWith(p))) {
        localStorage.removeItem(key);
      }
    }
  }
}

/**
 * Best-effort deletion of IndexedDB databases that may carry MTProto auth
 * material (auth keys, peer caches, passcode-encrypted blobs).
 */
export function purgeMtprotoIdbs() {
  const namesToKill = ['tt-session', 'passcodes', 'gramjs', 'gramjs-cache'];
  for (const name of namesToKill) {
    try {
      // `deleteDatabase` is async-but-fire-and-forget; we don't await it.
      indexedDB.deleteDatabase(name);
    } catch {
      // ignore
    }
  }
}

/**
 * Run the full lockdown. Must be called BEFORE any code that reads sessions
 * or subscribes to multitab broadcast channels.
 */
export function applySessionLockdown() {
  try { purgeMtprotoLeftovers(); } catch { /* ignore */ }
  try { purgeMtprotoIdbs(); } catch { /* ignore */ }
}
